// Viewer controller: owns the wasm renderer handle, the RAF loop, canvas sizing, and
// pointer/wheel input. The UI (App.tsx / Hud.tsx) only reads its signals and calls its
// methods — no nullable function refs wired across components.

import { createSignal, type Accessor } from "solid-js";

// The Rust renderer, compiled to wasm by `just build-wasm`. `init` boots the module;
// GsplatViewer is the handle we drive each frame. The wasm-bindgen glue fetches the
// hashed .wasm sitting next to it (dist/wasm/), so default init() just works.
import init, { GsplatViewer } from "./wasm/gsplat_core.js";

// Injected by build.ts (`define`): true for dev builds, false for minified release.
declare const __DEV__: boolean;

// A scene advertised by the Rust API (`GET /api/scenes`).
export interface Scene {
  name: string;
  file: string;
  url: string;
}

export interface Viewer {
  status: Accessor<string>;
  fps: Accessor<number>;
  splats: Accessor<number>;
  supported: Accessor<boolean>;
  activeScene: Accessor<string | null>;
  /** Boot wasm + WebGPU on this canvas and start the render loop. */
  attach(canvas: HTMLCanvasElement): Promise<void>;
  dispose(): void;
  setSortMode(mode: "cpu" | "radix"): void;
  setValidate(on: boolean): void;
  loadFile(file: File): Promise<void>;
  loadScene(scene: Scene): Promise<void>;
}

export function createViewer(): Viewer {
  const [status, setStatus] = createSignal("starting…");
  const [fps, setFps] = createSignal(0);
  const [splats, setSplats] = createSignal(0);
  const [supported, setSupported] = createSignal(true);
  const [activeScene, setActiveScene] = createSignal<string | null>(null);

  let viewer: GsplatViewer | null = null;
  let raf = 0;
  let ro: ResizeObserver | null = null;
  let detachInput: (() => void) | null = null;

  const attach = async (canvas: HTMLCanvasElement) => {
    if (!("gpu" in navigator)) {
      setSupported(false);
      setStatus("WebGPU not available in this browser");
      return;
    }

    await init();

    // Size the canvas to its CSS box × device pixel ratio (crisp on Retina).
    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        return { w, h };
      }
      return null;
    };
    fit();

    try {
      viewer = await GsplatViewer.init(canvas);
    } catch (e) {
      setStatus(`renderer init failed: ${e}`);
      return;
    }
    setStatus("rendering");
    if (__DEV__) {
      // Dev handle for console testing (e.g. gsplat.set_sort_mode('cpu')).
      (globalThis as unknown as { gsplat: GsplatViewer }).gsplat = viewer;
    }

    // Keep the drawing buffer in sync with layout changes.
    ro = new ResizeObserver(() => {
      const changed = fit();
      if (changed && viewer) viewer.resize(changed.w, changed.h);
    });
    ro.observe(canvas);

    // ── input: drag to orbit, wheel to zoom ──
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!dragging || !viewer) return;
      viewer.orbit(e.clientX - lastX, e.clientY - lastY);
      lastX = e.clientX;
      lastY = e.clientY;
    };
    const onPointerUp = () => {
      dragging = false;
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      viewer?.zoom(-e.deltaY * 0.001);
    };
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointerleave", onPointerUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    detachInput = () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointerleave", onPointerUp);
      canvas.removeEventListener("wheel", onWheel);
    };

    // ── render loop + FPS ──
    let last = performance.now();
    let frames = 0;
    const loop = (now: number) => {
      viewer?.render();
      frames++;
      if (now - last >= 500) {
        setFps(Math.round((frames * 1000) / (now - last)));
        frames = 0;
        last = now;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
  };

  const dispose = () => {
    cancelAnimationFrame(raf);
    ro?.disconnect();
    detachInput?.();
  };

  const loadBytes = (name: string, buf: Uint8Array) => {
    if (!viewer) throw new Error("renderer not ready yet");
    const n = viewer.load_ply(buf);
    setSplats(n);
    setStatus(`loaded ${name}`);
  };

  const loadFile = async (file: File) => {
    setActiveScene(null);
    setStatus(`loading ${file.name}…`);
    try {
      loadBytes(file.name, new Uint8Array(await file.arrayBuffer()));
    } catch (e) {
      setStatus(`load failed: ${e}`);
    }
  };

  const loadScene = async (scene: Scene) => {
    setActiveScene(scene.file);
    setStatus(`fetching ${scene.name}…`);
    try {
      const res = await fetch(scene.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      loadBytes(scene.name, new Uint8Array(await res.arrayBuffer()));
    } catch (e) {
      setStatus(`load failed: ${e}`);
      setActiveScene(null);
    }
  };

  return {
    status,
    fps,
    splats,
    supported,
    activeScene,
    attach,
    dispose,
    setSortMode: (m) => viewer?.set_sort_mode(m),
    setValidate: (on) => viewer?.set_validate(on),
    loadFile,
    loadScene,
  };
}
